import * as React from 'react'

import { ChevronLeft } from 'lucide-react'
import { observer } from 'mobx-react'

import Avatar from '~/client/src/shared/components/Avatar/Avatar'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
} from '~/client/src/shared/components/shadcn-ui/empty'
import { useFilePreview } from '~/client/src/shared/contexts/FilePreviewContext'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { ImageUploader } from '../core/ports'
import { Attachment, Chat, Contact, messageKey } from '../core/protocol/types'
import { AttachmentTrayStore } from '../core/stores/AttachmentTray.store'
import { ChatSession } from '../core/stores/Session'
import { toImagePreviewFiles } from '../web/attachmentPreview'
import { WebContactDirectory } from '../web/directory'
import ComposerBar from './ComposerBar'
import MessageBubble from './MessageBubble'

interface IProps {
  session: ChatSession
  directory: WebContactDirectory
  recipient: Contact
  projectDateStore: ProjectDateStore
  uploadImage: ImageUploader
  shouldSendOnEnter?: boolean
  onMaterialized: (chat: Chat) => void
  onMentionPress: (mentionedUserId: string) => void
  onBack: () => void
}

// A conversation that does not exist yet: composing here queues messages in
// the draft store; once the first send creates the channel the screen is
// replaced by the real conversation.
function DraftChatScreen({
  session,
  directory,
  recipient,
  projectDateStore,
  uploadImage,
  shouldSendOnEnter,
  onMaterialized,
  onMentionPress,
  onBack,
}: IProps): React.ReactElement {
  const [draft] = React.useState(() => session.openDraft(recipient))
  const [tray] = React.useState(
    () => new AttachmentTrayStore(uploadImage, session.config.newId),
  )
  const { showPreview } = useFilePreview()
  const listRef = React.useRef<HTMLDivElement>(null)

  const materializedChat = draft.materializedChat
  React.useEffect(() => {
    if (materializedChat) {
      onMaterialized(materializedChat)
    }
  }, [materializedChat, onMaterialized])

  // Every message here is the sender's own, so the list always follows the
  // newest one.
  React.useLayoutEffect(() => {
    const list = listRef.current
    if (list) {
      list.scrollTop = list.scrollHeight
    }
  }, [draft.messages])

  const handleSend = (message: string, attachments: Attachment[]): void => {
    draft.sendMessage(message, attachments)
  }

  const renderHeader = (): React.ReactElement => (
    <div className="tw:flex tw:items-center tw:gap-1.5 tw:px-4 tw:pb-5 tw:pt-3">
      <Button variant="primary-ghost" size="icon" onClick={onBack}>
        <ChevronLeft />
      </Button>
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:items-center">
        <span className="tw:max-w-full tw:truncate tw:text-base tw:font-semibold tw:text-foreground">
          {recipient.fullName}
        </span>
        <span className="tw:max-w-full tw:truncate tw:text-sm tw:text-muted-foreground">
          {directory.directSubtitle(recipient.id)}
        </span>
      </div>
      <Avatar
        src={recipient.avatarUrl}
        avatarType={AvatarType.User}
        isOnline={session.inbox.isOnline(recipient.id)}
      />
    </div>
  )

  const renderMessages = (): React.ReactElement => {
    if (!draft.messages.length) {
      return (
        <Empty className="tw:h-full">
          <EmptyHeader>
            <EmptyTitle>
              {Localization.translator.noMessagesYetSayHello}
            </EmptyTitle>
          </EmptyHeader>
        </Empty>
      )
    }

    return (
      <div
        ref={listRef}
        className="tw:flex tw:h-full tw:flex-col tw:overflow-y-auto tw:pb-2.5"
      >
        {draft.messages.map((message, index) => (
          <MessageBubble
            key={messageKey(message)}
            message={message}
            isOwn={true}
            isFirstOfRun={index === 0}
            isLastOfRun={index === draft.messages.length - 1}
            showAuthor={false}
            showAvatar={false}
            userId={session.config.userId}
            contacts={session.contacts}
            isAuthorOnline={false}
            projectDateStore={projectDateStore}
            onRetry={fakeId => draft.retry(fakeId)}
            onMentionPress={onMentionPress}
            onAttachmentPress={(attachments, index) =>
              showPreview(toImagePreviewFiles(attachments), index)
            }
          />
        ))}
      </div>
    )
  }

  return (
    <div className="tw:flex tw:h-full tw:flex-col">
      {renderHeader()}
      <div className="tw:min-h-0 tw:flex-1 tw:px-4">{renderMessages()}</div>
      <ComposerBar
        mentionCandidates={[recipient]}
        tray={tray}
        shouldSendOnEnter={shouldSendOnEnter}
        onSend={handleSend}
      />
    </div>
  )
}

export default observer(DraftChatScreen)
